import { XIcon } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import * as React from 'react'

import { platform } from '@/lib/platform'
import { cn } from '@/lib/utils'

// Closes whatever dialog is on top, the way Escape does, so its own onOpenChange runs.
const dismiss = () => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }))

// A dialog or sheet opens with focus on itself, not its first field: on a phone a focused field brings up the keyboard,
// which pushes the sheet out of view before anyone has asked to type. Tab and Escape work from there as before.
function focusSelf(e: Event) {
  e.preventDefault()
  ;(e.currentTarget as HTMLElement).focus()
}

// Inside a dialog (mounted only while it's open): on an Android phone the back gesture closes a sheet or dialog before it leaves the page, as it does in Android's
// own apps. An open dialog adds a step to the history for back to take; closing it any other way takes that step
// back off, unless the dialog went somewhere else as it closed.
export function BackCloses() {
  React.useEffect(() => {
    if (platform() !== 'android' || matchMedia('(min-width: 48.8125rem)').matches) return
    const step = `${Date.now()}-${Math.random()}`
    let popped = false
    const onPop = () => {
      // A dialog opened over this one took its own step back off: this one's step is on top again, so it stays.
      if (window.history.state?.sheet === step) return
      popped = true
      dismiss()
    }
    // A moment later, so React's development double-mount doesn't leave a step behind.
    const timer = window.setTimeout(() => {
      window.history.pushState({ ...window.history.state, sheet: step }, '')
      window.addEventListener('popstate', onPop)
    })
    return () => {
      window.clearTimeout(timer)
      window.removeEventListener('popstate', onPop)
      if (!popped && window.history.state?.sheet === step) window.history.back()
    }
  }, [])
  return null
}

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-black/30 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  onOpenAutoFocus = focusSelf,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & { showCloseButton?: boolean }) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        onOpenAutoFocus={onOpenAutoFocus}
        className={cn(
          'fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-3xl bg-background p-6 shadow-lift duration-200 outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 sm:max-w-md',
          className,
        )}
        {...props}
      >
        <SheetHandle />
        <BackCloses />
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="absolute top-3 right-3 grid size-11 place-items-center rounded-full text-muted-foreground transition-colors outline-none hover:bg-secondary hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 [&_svg]:size-4"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

// On a phone a dialog is a sheet (styles/phone.css), and like a phone's own sheets it can be pulled down by its handle
// to put it away. A pull past a third of the way, or a flick, closes it; less springs back. The close button and
// Escape do the same for everyone else, so the handle itself is hidden from assistive technology.
function SheetHandle() {
  const drag = React.useRef<{ y: number; at: number; sheet: HTMLElement } | null>(null)
  const move = (e: React.PointerEvent) => {
    if (!drag.current) return
    const dy = Math.max(0, e.clientY - drag.current.y)
    drag.current.sheet.style.translate = `0 ${dy}px`
  }
  const end = (e: React.PointerEvent) => {
    const d = drag.current
    if (!d) return
    drag.current = null
    const dy = Math.max(0, e.clientY - d.y)
    const flick = dy / Math.max(1, e.timeStamp - d.at) > 0.6
    d.sheet.style.transition = 'translate 0.24s var(--ease-page)'
    if (dy > d.sheet.offsetHeight / 3 || (flick && dy > 24)) {
      d.sheet.style.translate = '0 100%'
      // The dialog closes the way it would for Escape, so every dialog's own onOpenChange runs.
      window.setTimeout(dismiss, 200)
    } else {
      d.sheet.style.translate = ''
    }
  }
  return (
    <div
      data-slot="sheet-handle"
      aria-hidden="true"
      className="absolute inset-x-0 top-0 hidden h-8 cursor-grab touch-none justify-center"
      onPointerDown={(e) => {
        const sheet = e.currentTarget.closest<HTMLElement>('[data-slot=dialog-content]')
        if (!sheet) return
        e.currentTarget.setPointerCapture(e.pointerId)
        sheet.style.transition = 'none'
        drag.current = { y: e.clientY, at: e.timeStamp, sheet }
      }}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
    >
      <span className="mt-2 h-1.25 w-9 rounded-full bg-muted-foreground/35" />
    </div>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="dialog-header" className={cn('flex flex-col gap-1.5', className)} {...props} />
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-title font-semibold tracking-tight', className)}
      {...props}
    />
  )
}

function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-sub text-muted-foreground', className)}
      {...props}
    />
  )
}

export { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger }
